import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { STAFF_ROLES } from '../config/features';
import StoreLayout from './StoreLayout';
import StaffProductPanel from './StaffProductPanel';
import { CHECKOUT_ENABLED, CORPORATE } from './storeConfig';
import OptionPicker from './OptionPicker';
import QtyStepper from './QtyStepper';
import { ProductRail, WishlistButton } from './ProductCard';
import { usePurchase } from './usePurchase';
import { fetchProduct, useProducts, useProductsByIds } from './catalogApi';
import { markViewed, useRecentlyViewed } from './savedLists';
import { categoryLink, formatInr, newestIds, retailPrice, useDocumentTitle, variantText } from './retailUtils';
import { CheckIcon, ShareIcon, TagIcon, TruckIcon } from './Icons';

const LOW_STOCK = 5;

/** Shares the product's clean link (rare.eyantra.com/product/130), never the address bar's: the
 *  phone's share sheet on touch screens, else copies it (as most stores' desktop Share does). */
function ShareButton({ product }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/product/${product.id}`;
    try {
      if (window.matchMedia?.('(pointer: coarse)').matches && navigator.share) {
        await navigator.share({ title: product.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* share sheet closed, or clipboard blocked */ }
  };
  return (
    <button type="button" className="rt-pdp-heart rt-share" aria-label="Share" onClick={share}>
      <ShareIcon size={20} />
      {copied && <span className="rt-share-copied" role="status">Link copied</span>}
    </button>
  );
}

function useSwipe(onPrev, onNext) {
  const x = useRef(null);
  return {
    onTouchStart: (e) => { x.current = e.touches[0].clientX; },
    onTouchEnd: (e) => {
      if (x.current == null) return;
      const dx = e.changedTouches[0].clientX - x.current;
      x.current = null;
      if (Math.abs(dx) > 40) (dx < 0 ? onNext : onPrev)();
    },
  };
}

/** Full-screen image viewer: swipe/arrow keys between images, tap to zoom (pinch works natively). */
function Lightbox({ images, index, onIndex, onClose, title }) {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState('50% 50%');
  const count = images.length;
  const go = (d) => { setZoom(false); onIndex((index + d + count) % count); };
  const swipe = useSwipe(() => go(-1), () => go(1));
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  });
  return (
    <div className="rt-lightbox" role="dialog" aria-modal="true" aria-label={`${title} images`}>
      <button type="button" className="rt-lightbox-close" aria-label="Close" onClick={onClose} autoFocus>✕</button>
      <div className="rt-lightbox-stage" {...(zoom ? {} : swipe)}
        onMouseMove={e => { if (!zoom) return; const r = e.currentTarget.getBoundingClientRect(); setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }}>
        <img src={images[index]} alt={`${title}, ${index + 1} of ${count}`}
          className={zoom ? 'is-zoomed' : ''} style={{ transformOrigin: origin }} onClick={() => setZoom(z => !z)} />
      </div>
      {count > 1 && <>
        <button type="button" className="rt-lightbox-nav rt-lightbox-prev" aria-label="Previous image" onClick={() => go(-1)}>‹</button>
        <button type="button" className="rt-lightbox-nav rt-lightbox-next" aria-label="Next image" onClick={() => go(1)}>›</button>
        <div className="rt-lightbox-count">{index + 1} / {count}</div>
      </>}
    </div>
  );
}

export default function StoreProductPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const recentIds = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const p = usePurchase(product);
  // Until this product has loaded (the previous one is still in state for a moment when moving
  // between products), the title isn't ready, so the page view waits for it.
  useDocumentTitle(notFound ? null
    : product && String(product.id) === String(productId) ? product.name : undefined);

  useEffect(() => {
    setProduct(null); setNotFound(false); setImageIndex(0); setLightbox(false);
    fetchProduct(productId)
      .then(data => {
        // Removed from the shop (kept only for existing carts and orders): an old link or a
        // saved bag shows "isn't available" instead of a product that can't be bought.
        if (data?.approvalStatus === 'REMOVED') { setNotFound(true); return; }
        setProduct(data);
        markViewed(data.id);
      })
      .catch(() => setNotFound(true));
  }, [productId]);

  // Product photos plus any colour photo not already among them, in a fixed order, so
  // picking a colour moves to its photo instead of reshuffling the gallery.
  const images = useMemo(() => {
    if (!product) return [];
    const list = product.imageUrls?.length ? product.imageUrls : (product.thumbnailUrl ? [product.thumbnailUrl] : []);
    return [...new Set([...list, ...(product.variants || []).map(v => v.imageUrl).filter(Boolean)])];
  }, [product]);
  // Photo URL → the colour it shows. A photo used by two colours (a group shot) maps to
  // null, so viewing it never changes the selected colour.
  const colorByImage = useMemo(() => {
    const map = new Map();
    (product?.variants || []).forEach(v => {
      if (!v.color || !v.imageUrl) return;
      map.set(v.imageUrl, map.has(v.imageUrl) && map.get(v.imageUrl) !== v.color ? null : v.color);
    });
    return map;
  }, [product]);
  // Resized variants (when the image resizer is configured): URL → srcset, so each device
  // downloads only the width it needs. Absent srcsets fall back to the plain URL.
  const srcsets = useMemo(() => {
    const map = new Map();
    (product?.imageUrls || []).forEach((u, i) => product.imageSrcsets?.[i] && map.set(u, product.imageSrcsets[i]));
    (product?.variants || []).forEach(v => v.imageUrl && v.imageSrcset && map.set(v.imageUrl, v.imageSrcset));
    return map;
  }, [product]);
  // Colour → photo: picking a colour shows its photo, unless a photo of that colour is
  // already showing (e.g. the shopper got here by clicking that photo).
  useEffect(() => {
    if (!p.color || !p.variantImage) return;
    setImageIndex(i => {
      if (colorByImage.get(images[i]) === p.color) return i;
      const j = images.indexOf(p.variantImage);
      return j >= 0 ? j : i;
    });
  }, [p.color, p.variantImage, images, colorByImage]);
  // Photo → colour: every way of changing photo (thumbnail, arrows, swipe, lightbox) goes
  // through here, so viewing a colour's photo selects that colour if it's in stock.
  const showImage = (i) => {
    if (!images.length) return;
    const n = (i + images.length) % images.length;
    setImageIndex(n);
    const c = colorByImage.get(images[n]);
    if (c && c !== p.color && p.options.colors.some(o => o.name === c && o.available)) p.pickColor(c);
  };
  const swipe = useSwipe(() => showImage(imageIndex - 1), () => showImage(imageIndex + 1));

  // Same type first, then the rest of the category.
  const { products: sameType } = useProducts(product ? { category: product.category, subcategory: product.subcategory } : null, 13);
  const { products: sameCategory } = useProducts(product ? { category: product.category } : null, 13);
  const similar = useMemo(() => {
    if (!product) return [];
    const seen = new Set([product.id]);
    return [...sameType, ...sameCategory]
      .filter(c => c.itemsAvailable !== 0 && !seen.has(c.id) && seen.add(c.id))
      .slice(0, 12);
  }, [product, sameType, sameCategory]);
  const { products: recent } = useProductsByIds(recentIds.filter(id => String(id) !== String(productId)).slice(0, 12));

  if (notFound) {
    return (
      <StoreLayout>
        <div className="rt-empty">
          <h2>This product isn't available</h2>
          <p>It may have sold out or been removed.</p>
          <Link to="/catalog" className="rt-btn" style={{ marginTop: 12 }}>Continue shopping</Link>
        </div>
      </StoreLayout>
    );
  }
  if (!product) {
    return (
      <StoreLayout>
        <div className="rt-pdp" aria-busy="true">
          <div className="rt-skel" style={{ aspectRatio: '4 / 5', borderRadius: 14 }} />
          <div>
            <div className="rt-skel" style={{ height: 14, width: '30%' }} />
            <div className="rt-skel" style={{ height: 30, marginTop: 12 }} />
            <div className="rt-skel" style={{ height: 24, width: '40%', marginTop: 16 }} />
            <div className="rt-skel" style={{ height: 48, marginTop: 28 }} />
          </div>
        </div>
      </StoreLayout>
    );
  }

  const { price, mrp, hasDiscount, discountPercent } = retailPrice(product);
  const cantAdd = p.soldOut || (p.complete && p.maxAdd <= 0);
  const mainImage = images[Math.min(imageIndex, images.length - 1)];
  const dims = product.dimensionL && product.dimensionW && product.dimensionH
    ? `${product.dimensionL} × ${product.dimensionW} × ${product.dimensionH} ${product.dimensionUnit || ''}`.trim()
    : null;
  const newIds = newestIds(similar);

  return (
    <StoreLayout wide>
      <nav className="rt-crumbs rt-pad" aria-label="Breadcrumb">
        <Link to="/">Home</Link>›
        {product.category && <><Link to={categoryLink(product.category)}>{product.category}</Link>›</>}
        {product.subcategory && <><Link to={categoryLink(product.category, product.subcategory)}>{product.subcategory}</Link>›</>}
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="rt-pdp">
        {/* Desktop: one photo at a time with a thumbnail column, so picking a colour
            visibly switches the photo. */}
        <div className={`rt-gallery rt-only-desktop${images.length < 2 ? ' rt-gallery--single' : ''}`}>
          {images.length > 1 && (
            <div className="rt-gallery-thumbs">
              {images.map((url, i) => (
                <button key={url} type="button" className={`rt-gallery-thumb${i === imageIndex ? ' is-on' : ''}`}
                  aria-label={`Show image ${i + 1}`} aria-current={i === imageIndex} onClick={() => showImage(i)}>
                  <img src={url} srcSet={srcsets.get(url)} sizes="160px" alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          <div className="rt-gallery-main">
            {mainImage && (
              <button type="button" className="rt-main-img-btn" aria-label="Open full-screen images" onClick={() => setLightbox(true)}>
                {/* The photo is ~50vw; asking for 100vw picks a file ~2× the slot, which keeps
                    fabric texture a slot-sized file smooths away. */}
                <img src={mainImage} srcSet={srcsets.get(mainImage)} sizes="100vw" alt={product.name} />
              </button>
            )}
            {images.length > 1 && <>
              <button type="button" className="rt-gallery-nav rt-gallery-prev" aria-label="Previous image" onClick={() => showImage(imageIndex - 1)}>‹</button>
              <button type="button" className="rt-gallery-nav rt-gallery-next" aria-label="Next image" onClick={() => showImage(imageIndex + 1)}>›</button>
            </>}
          </div>
        </div>
        {/* Phones: swipeable single photo with position dots. */}
        <div className="rt-main-img rt-only-mobile" {...swipe}>
          {mainImage && (
            <button type="button" className="rt-main-img-btn" aria-label="Open full-screen images" onClick={() => setLightbox(true)}>
              <img src={mainImage} srcSet={srcsets.get(mainImage)} sizes="100vw" alt={product.name} />
            </button>
          )}
          {images.length > 1 && (
            <div className="rt-img-dots" aria-hidden="true">
              {images.map((url, i) => <span key={url} className={i === imageIndex ? 'on' : ''} />)}
            </div>
          )}
        </div>

        <div className="rt-pdp-info">
          <div className="rt-pdp-titlerow">
            <h1 className="rt-pdp-name">{product.name}</h1>
            <div className="rt-pdp-actions">
              <ShareButton product={product} />
              <WishlistButton productId={product.id} className="rt-pdp-heart" size={20} />
            </div>
          </div>
          {product.brandName && <div className="rt-pdp-brand">{product.brandName}</div>}
          <div className="rt-price">
            <span className="rt-price-now">{formatInr(price)}</span>
            {hasDiscount && <>
              <span className="rt-price-mrp"><span className="rt-sr-only">MRP </span>{formatInr(mrp)}</span>
              <span className="rt-price-off">{discountPercent}% off</span>
            </>}
            <span className="rt-tax-note">Incl. of all taxes</span>
          </div>

          <OptionPicker idPrefix="pdp" options={p.options} color={p.color} size={p.size}
            onColor={p.pickColor} onSize={p.pickSize} missing={p.missing} />

          {!p.soldOut && (
            <div className="rt-opt">
              <div className="rt-opt-label">Quantity</div>
              <QtyStepper label="Quantity" value={p.qty} max={Math.max(1, p.maxAdd)} onChange={p.setQuantity} />
              {p.stock != null && p.stock > 0 && p.stock <= LOW_STOCK && (
                <div className="rt-stock rt-stock--low">Only {p.stock} left{p.complete && variantText(p.line) ? ` in ${variantText(p.line)}` : ''}</div>
              )}
              {p.inBag > 0 && <div className="rt-stock">{p.inBag} already in your bag{p.maxAdd <= 0 ? ' — that’s all we have' : ''}</div>}
            </div>
          )}

          <div className="rt-pdp-cta">
            <div className="rt-pdp-cta-price">
              <strong>{formatInr(price)}</strong>
              {hasDiscount && <span className="rt-price-mrp">{formatInr(mrp)}</span>}
            </div>
            {p.soldOut ? (
              <button type="button" className="rt-btn" disabled>Sold out</button>
            ) : (
              <>
                <button type="button" className="rt-btn" disabled={cantAdd} onClick={p.addAndOpenBag}>Add to bag</button>
                {CHECKOUT_ENABLED && <button type="button" className="rt-btn rt-btn--ghost" disabled={cantAdd} onClick={p.buyNow}>Buy now</button>}
              </>
            )}
          </div>

          {/* No delivery dates are promised (see CLAUDE.md, business context). */}
          <ul className="rt-assurances">
            <li><TruckIcon /> Delivery across India</li>
            <li><CheckIcon /> Prices include GST</li>
            <li><TagIcon /> <span>Buying in bulk or for your company? <Link to={CORPORATE.path} className="rt-link">Corporate orders</Link></span></li>
          </ul>

          {(product.description || dims) && (
            <details className="rt-acc" open>
              <summary>Product details</summary>
              {product.description && <p>{product.description}</p>}
              {dims && <p><strong>Dimensions:</strong> {dims}</p>}
            </details>
          )}

          {isStaff && (
            <StaffProductPanel product={product} role={user.role} line={p.complete ? p.line : null}
              quantity={p.qty} onProductChange={setProduct} />
          )}
        </div>
      </div>

      <div className="rt-pad">
        <ProductRail title="You may also like" products={similar} newIds={newIds} />
        <ProductRail title="Recently viewed" products={recent} newIds={newIds} />
      </div>

      {lightbox && images.length > 0 && (
        <Lightbox images={images} index={imageIndex} onIndex={showImage} onClose={() => setLightbox(false)} title={product.name} />
      )}
    </StoreLayout>
  );
}
